import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import { api } from '../utils/electronAPI';
import { ladeBetriebe } from '../utils/betrieb';
import { lohnsaetzeAktualisieren } from '../utils/kalkulation';
import { offeneRechtstexte } from '../utils/rechtstexte';
import { nutzungStarten, nutzungBeenden } from '../utils/nutzung';
import store from '../app/store';
import { setAllSettings, KALKULATION_DEFAULTS } from '../features/settings/settingsSlice';

const aktuellesToken = async () => (await supabase.auth.getSession()).data.session?.access_token;

async function loadSettingsIntoRedux() {
    try {
        const data = await api.getSettings();
        if (data) {
            store.dispatch(setAllSettings({
                aiOptimizationEnabled: Boolean(data.ai_optimization_enabled),
                customAiPrompt: data.custom_ai_prompt || '',
                companyName: data.company_name || '',
                currency: data.currency || 'EUR',
                kalkulation: { ...KALKULATION_DEFAULTS, ...(data.kalkulation || {}) },
                company: data.company || {},
                workspaceMode: data.workspace_mode || 'demo',
                onboarding: data.onboarding || { completed: false },
                briefpapier: data.briefpapier || {},
            }));
            return data;
        }
    } catch (_) {}
    return null;
}

const AuthContext = createContext({});

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [passwordRecovery, setPasswordRecovery] = useState(false);
    // Das Onboarding-Gate darf erst entscheiden, wenn die Settings wirklich da sind —
    // sonst blitzt die App kurz auf, bevor der Wizard übernimmt.
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    // Betrieb zuerst: Settings, Storage-Pfade und alle Daten hängen daran.
    const [betrieb, setBetrieb] = useState(null);
    const [betriebe, setBetriebe] = useState([]);
    const [istOpteroTeam, setIstOpteroTeam] = useState(false);
    // Neue Version der Nutzungsbedingungen o. Ä. → Inhaber stimmt vor allem anderen zu
    const [offeneTexte, setOffeneTexte] = useState([]);
    const letzterNutzer = useRef(null);

    const ladeKontext = async () => {
        try {
            const k = await ladeBetriebe();
            setBetrieb(k.aktuell);
            setBetriebe(k.alle);
            setIstOpteroTeam(k.istOpteroTeam);
            if (k.aktuell?.rolle === 'inhaber' && !k.istOpteroTeam) {
                setOffeneTexte(await offeneRechtstexte().catch((e) => {
                    console.error('[Auth] Rechtstexte:', e.message);
                    return [];
                }));
            }
            if (k.aktuell) {
                nutzungStarten(aktuellesToken);
                await loadSettingsIntoRedux();
                await lohnsaetzeAktualisieren().catch((e) => console.error('[Auth] Lohnsätze:', e.message));
            }
        } catch (e) {
            console.error('[Auth] Betrieb laden:', e);
        } finally {
            setSettingsLoaded(true);
        }
    };

    useEffect(() => {
        supabase.auth.getSession().then(async ({ data: { session } }) => {
            // Wenn Session vorhanden aber Token abgelaufen → automatisch refreshen
            if (session && session.expires_at && session.expires_at * 1000 < Date.now()) {
                const { data } = await supabase.auth.refreshSession();
                session = data.session;
            }
            setUser(session?.user ?? null);
            setLoading(false);
            if (session?.user) {
                ladeKontext();
            } else {
                setSettingsLoaded(true);
            }
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            // Anderes Konto im selben Browser (zweiter Tab, Einladungslink): Ein offener Tab
            // hätte sonst Rolle und Einstellungen des alten Kontos, aber das Token des neuen.
            const vorher = letzterNutzer.current;
            letzterNutzer.current = session?.user?.id ?? null;
            if (vorher && session?.user && session.user.id !== vorher) {
                window.location.reload();
                return;
            }
            setUser(session?.user ?? null);
            if (event === 'SIGNED_IN' && session?.user) {
                // Supabase feuert SIGNED_IN auch beim Tab-Fokus — den Kontext
                // nicht jedes Mal neu laden, nur wenn noch keiner da ist.
                setBetrieb((b) => {
                    if (!b) ladeKontext();
                    return b;
                });
            }
            if (event === 'SIGNED_OUT') {
                nutzungBeenden();
                setBetrieb(null);
                setBetriebe([]);
                setOffeneTexte([]);
            }
            if (event === 'PASSWORD_RECOVERY') {
                setPasswordRecovery(true);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const signOut = async () => {
        await nutzungBeenden();
        // Nur dieses Gerät — wer auf mehreren angemeldet ist, bleibt dort angemeldet
        await supabase.auth.signOut({ scope: 'local' });
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{
            user, loading, signOut, passwordRecovery, setPasswordRecovery,
            settingsLoaded, reloadSettings: loadSettingsIntoRedux,
            betrieb, betriebe, istOpteroTeam, reloadBetriebe: ladeKontext,
            offeneTexte, setOffeneTexte,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
